// =========================================================
// Citation source registry — cite-id → source metadata
// =========================================================
//
// Resolves citation labels produced by AI answers into a renderable source
// snapshot: human title, an excerpt for the drawer, and citation metadata
// (date, author, confidence).
//
// Consumer: the AIRail's answer citations (CitationChip → 'citation' drawer).
// New cite IDs land here. The mock corpus is intentionally small — replace
// these demo sources with your product's reference documents.
//
// paul-fmea.A6 (#273): PB_RECOVERY_CITATION_SOURCES are generated, not
// hand-typed — see rule-set-retrieval.ts. They cite `rs-vN · dr-*` rule-set
// rows, not documents, and open the actual live rule row.

import { PB_RECOVERY_CITATION_SOURCES } from './rule-set-retrieval';

export type CitationConfidence = 'high' | 'medium' | 'inferred';

export type CitationSource = {
  /** Stable id used inside citation labels. */
  id: string;
  /** Human title for the chip's hover preview header. */
  title: string;
  /** Source filename shown in the drawer. */
  documentFilename: string;
  /** Excerpt rendered in the citation drawer. */
  excerpt: string;
  /** ISO date of the source. */
  date: string;
  author: string;
  confidence: CitationConfidence;
};

const SOURCES: CitationSource[] = [
  {
    id: 'DOC-001',
    title: 'Product design brief',
    documentFilename: 'design-brief.pdf',
    excerpt:
      'The workspace uses a three-panel layout: sidebar on the left, the active scene in the centre, and the docked chat rail on the right.',
    date: '2026-07-01',
    author: 'Design team',
    confidence: 'high',
  },
  {
    id: 'DOC-002',
    title: 'Requirements note — v1 scope',
    documentFilename: 'requirements-v1.md',
    excerpt:
      'v1 ships the scaffold only: navigation chrome, one example module, the build-doc drawer, and the comments overlay.',
    date: '2026-07-15',
    author: 'Product team',
    confidence: 'medium',
  },
  {
    id: 'A',
    title: 'Metal accounting design-basis workbook cross-check (A §M2 §6.2)',
    documentFilename: 'site-mass-balance-workbook.xlsx',
    excerpt:
      'Same fresh feed (1,800 stpd) and design feed grades feed two independent calculation paths for daily contained metal in each final concentrate. All three circuits agree within 0.3%.',
    date: '2026-06-08',
    author: 'Functional Description + Mass Balance workbook',
    confidence: 'high',
  },
  {
    id: 'B: slide 05',
    title: 'Stakeholder deck — slide 05 (AI Performance)',
    documentFilename: 'site-deck-slide-05.png',
    excerpt:
      'Recovery (24h avg) 82.3% vs design 85.1%; Zn circuit pH in-spec only 68% of this shift; grinding utilization and bottleneck read for the plant model dashboard.',
    date: '2026-06-08',
    author: 'Stakeholder deck',
    confidence: 'high',
  },
  {
    id: 'B: slide 07',
    title: 'Stakeholder deck — slide 07 (Metallurgist — sample register & lab compliance)',
    documentFilename: 'site-deck-slide-07.png',
    excerpt:
      'Mass Balance Closure · Today (tolerance ±3%): Zn +1.8% CLOSED · Pb −2.4% CLOSED · Ag −5.1% REVIEW',
    date: '2026-06-08',
    author: 'Stakeholder deck',
    confidence: 'high',
  },
  {
    id: 'FMEA-IRA',
    title: 'FMEA immediate-response ruleset',
    documentFilename: 'site-fmea-workbook.xlsx',
    excerpt:
      'IF Zn float pH < SP low limit THEN increase lime SP to Zn circuit — Immediate Response routed to the CRO (zero-touch). Under-liming attaches with no investigation step.',
    date: '2026-06-08',
    author: 'FMEA workbook · A §M3',
    confidence: 'high',
  },
  ...PB_RECOVERY_CITATION_SOURCES,
];

const BY_ID = new Map(SOURCES.map((s) => [s.id, s]));

export function getCitationSource(id: string): CitationSource | undefined {
  return BY_ID.get(id);
}

export function allCitationSources(): readonly CitationSource[] {
  return SOURCES;
}
